import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Clock, Users, TrendingUp, Download, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';

const TimeTrackingAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, completed

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [entriesRes, summaryRes] = await Promise.all([
        axios.get('/admin/time/entries'),
        axios.get('/admin/time/summary')
      ]);
      
      setEntries(entriesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error loading time tracking data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Möchten Sie diesen Eintrag wirklich löschen?')) return;

    try {
      await axios.delete(`/admin/time/${entryId}`);
      toast.success('Eintrag gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleExport = () => {
    // Create CSV
    const headers = ['Datum', 'Mitarbeiter', 'Einstempeln', 'Ausstempeln', 'Pause (Min)', 'Gesamtstunden', 'Status', 'Notizen'];
    const rows = filteredEntries.map(entry => [
      new Date(entry.clock_in).toLocaleDateString('de-DE'),
      entry.user_name,
      new Date(entry.clock_in).toLocaleTimeString('de-DE'),
      entry.clock_out ? new Date(entry.clock_out).toLocaleTimeString('de-DE') : '-',
      entry.break_minutes || 0,
      entry.total_hours || 0,
      entry.status === 'active' ? 'Aktiv' : 'Abgeschlossen',
      entry.notes || ''
    ]);

    const csv = [headers, ...rows].map(row => row.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `zeiterfassung_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success('CSV-Datei heruntergeladen');
  };

  const formatDuration = (hours) => {
    if (!hours) return '0:00';
    const h = Math.floor(Math.abs(hours));
    const m = Math.round((Math.abs(hours) - h) * 60);
    return `${hours < 0 ? '-' : ''}${h}:${m.toString().padStart(2, '0')}`;
  };

  const groupByUser = (entries) => {
    const grouped = {};
    entries.forEach(entry => {
      const userId = entry.user_id;
      if (!grouped[userId]) {
        grouped[userId] = {
          user_name: entry.user_name,
          entries: []
        };
      }
      grouped[userId].entries.push(entry);
    });
    return grouped;
  };

  const filteredEntries = entries.filter(entry => {
    const matchesSearch = entry.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (entry.notes && entry.notes.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || entry.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const groupedByUser = groupByUser(filteredEntries);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white flex items-center gap-3">
            <Clock className="h-10 w-10" />
            Arbeitszeiterfassung
          </h1>
          <p className="text-zinc-400 mt-2">Verwaltung aller Zeiterfassungen</p>
        </div>
        
        <Button onClick={handleExport} size="lg">
          <Download className="mr-2 h-5 w-5" />
          CSV Export
        </Button>
      </div>

      {/* Statistics */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-400">Gesamtstunden</p>
                  <p className="text-2xl font-bold text-white">
                    {formatDuration(summary.total_hours)}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-400">Einträge</p>
                  <p className="text-2xl font-bold text-white">{summary.total_entries}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-400">Aktive Schichten</p>
                  <p className="text-2xl font-bold text-white">{summary.active_shifts}</p>
                </div>
                <Users className="h-8 w-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-400">Mitarbeiter</p>
                  <p className="text-2xl font-bold text-white">{summary.user_stats.length}</p>
                </div>
                <Users className="h-8 w-8 text-orange-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-zinc-400" />
                <Input
                  placeholder="Suche nach Name oder Notizen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-zinc-950 border-zinc-800 text-white"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant={filterStatus === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('all')}
              >
                Alle
              </Button>
              <Button
                variant={filterStatus === 'active' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('active')}
              >
                Aktiv
              </Button>
              <Button
                variant={filterStatus === 'completed' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('completed')}
              >
                Abgeschlossen
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Entries by User */}
      <div className="space-y-4">
        {Object.keys(groupedByUser).length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-12 text-center">
              <Clock className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Keine Einträge gefunden</h3>
              <p className="text-zinc-400">
                {searchTerm || filterStatus !== 'all' 
                  ? 'Versuchen Sie andere Suchkriterien' 
                  : 'Es wurden noch keine Zeiten erfasst'}
              </p>
            </CardContent>
          </Card>
        ) : (
          Object.entries(groupedByUser).map(([userId, userData]) => {
            const userTotal = userData.entries
              .filter(e => e.status === 'completed')
              .reduce((sum, e) => sum + (e.total_hours || 0), 0);
            
            return (
              <Card key={userId} className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      {userData.user_name}
                    </CardTitle>
                    <Badge className="bg-blue-900/30 text-blue-400 border-blue-700">
                      {formatDuration(userTotal)} • {userData.entries.length} Einträge
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {userData.entries.sort((a, b) => new Date(b.clock_in) - new Date(a.clock_in)).map(entry => (
                      <div
                        key={entry.entry_id}
                        className={`p-4 rounded-lg flex items-start justify-between ${
                          entry.status === 'active' 
                            ? 'bg-green-900/20 border-2 border-green-700' 
                            : 'bg-zinc-800 border-2 border-transparent'
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <p className="font-semibold text-white">
                              {new Date(entry.clock_in).toLocaleDateString('de-DE')}
                              {' • '}
                              {new Date(entry.clock_in).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                              {' → '}
                              {entry.clock_out 
                                ? new Date(entry.clock_out).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
                                : 'Aktiv'
                              }
                            </p>
                            {entry.status === 'active' && (
                              <Badge className="bg-green-900/30 text-green-400 border-green-700">
                                Läuft
                              </Badge>
                            )}
                          </div>
                          
                          {entry.total_hours !== null && (
                            <p className="text-sm text-zinc-400">
                              Dauer: <span className="font-semibold text-white">{formatDuration(entry.total_hours)}</span>
                              {entry.break_minutes > 0 && ` (${entry.break_minutes} Min. Pause)`}
                            </p>
                          )}
                          
                          {entry.notes && (
                            <p className="text-sm text-zinc-500 mt-1">{entry.notes}</p>
                          )}
                        </div>
                        
                        {entry.status === 'completed' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(entry.entry_id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TimeTrackingAdmin;
